import React, { useRef } from 'react';
import { Box, Button, HStack, IconButton, Input, Text, VStack } from '@chakra-ui/react';
import { FiX } from 'react-icons/fi';
import { addSpan, splitBySpans } from './correctionTypes';

// Character offset of a DOM point inside container, counting text only.
export const offsetWithin = (container, node, offset) => {
  const range = document.createRange();
  range.selectNodeContents(container);
  range.setEnd(node, offset);
  return range.toString().length;
};

// Select words with the mouse to mark them red; each mark gets its own
// "right words" box when withRight is set (checklist items 14-15).
export default function MarkableText({ text, spans, onChange, withRight = true, lang = 'zh' }) {
  const zh = lang === 'zh';
  const boxRef = useRef(null);

  const markSelection = () => {
    const selection = window.getSelection?.();
    const box = boxRef.current;
    if (!selection || selection.isCollapsed || !box || !selection.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (!box.contains(range.startContainer) || !box.contains(range.endContainer)) return;
    const start = offsetWithin(box, range.startContainer, range.startOffset);
    const end = offsetWithin(box, range.endContainer, range.endOffset);
    const next = addSpan(spans, text, start, end);
    if (next !== spans) onChange(next);
    selection.removeAllRanges();
  };

  const update = (index, right) => onChange(spans.map((s, i) => (i === index ? { ...s, right } : s)));
  const remove = (index) => onChange(spans.filter((_, i) => i !== index));

  return (
    <VStack align="stretch" spacing={3}>
      <Box
        ref={boxRef} onMouseUp={markSelection} onTouchEnd={markSelection}
        p={3} bg="gray.50" border="1px solid" borderColor="gray.200" rounded="md"
        fontSize="md" lineHeight="1.8" cursor="text" userSelect="text" data-testid="markable-text"
      >
        {splitBySpans(text, spans).map((piece, i) => (piece.span
          ? <Box as="mark" key={i} bg="red.100" color="red.700" px="1px" rounded="sm">{piece.text}</Box>
          : <React.Fragment key={i}>{piece.text}</React.Fragment>))}
      </Box>
      <Text fontSize="xs" color="gray.500">
        {zh ? '用鼠标拖选有问题的字，松开即标红；可以标多处。' : 'Drag across the wrong words to mark them; you can mark several.'}
      </Text>
      {spans.map((span, index) => (
        <HStack key={`${span.start}-${span.end}`} spacing={2} align="center">
          <Text fontSize="sm" color="red.600" minW="90px" noOfLines={1} title={span.wrong}>【{span.wrong}】</Text>
          {withRight && (
            <>
              <Text fontSize="sm" color="gray.500">→</Text>
              <Input
                size="sm" value={span.right || ''} maxLength={500}
                placeholder={zh ? '正确内容' : 'Right words'}
                onChange={(e) => update(index, e.target.value)}
              />
            </>
          )}
          <IconButton size="xs" variant="ghost" aria-label={zh ? '取消标记' : 'Remove mark'} icon={<FiX />} onClick={() => remove(index)} />
        </HStack>
      ))}
      {spans.length > 0 && (
        <Button size="xs" variant="link" alignSelf="flex-start" onClick={() => onChange([])}>{zh ? '清除全部标记' : 'Clear marks'}</Button>
      )}
    </VStack>
  );
}
